import { useState, useEffect, useMemo } from 'react';
import { useOrder } from '../hooks/use-order';
import { useOrderTimeline } from '../hooks/use-order-timeline';
import { useUpdateOrderStatus } from '../hooks/use-update-order-status';
import { useRefundOrder } from '../hooks/use-refund-order';
import { SellerRefundModal } from '../components/seller-refund-modal';
import { DeliveryTrackingManager } from '../components/DeliveryTrackingManager';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/molecules/select';
import { useTranslation } from 'react-i18next';
import { formatDate, formatPrice, getAmountFromPriceObject } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { ArrowLeft, Package, Truck, User, History, MessageSquare } from 'lucide-react';
import { OrderTimeline } from '@/components/organisms/timeline';
import { Link } from 'react-router-dom';
import type { OrderStatus, Order, OrderTracking } from '@/lib/types';

interface OrderDetailContainerProps {
  orderId: string;
}

const statusVariantMap: Record<OrderStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  waiting_validation: 'outline',
  validated: 'secondary',
  in_progress: 'secondary',
  in_delivery: 'default',
  delivered: 'default',
  ready_to_pick_up: 'secondary',
  canceled: 'destructive',
  canceled_by_system: 'destructive',
  canceled_by_admin: 'destructive',
  canceled_by_customer: 'destructive',
  canceled_by_seller: 'destructive',
};

// Valid status transitions based on server business rules
// See: serveur-marketplace/src/modules/order/services/order.service.ts
// Note: 'validated' auto-transitions to 'in_progress' on the server
const VALID_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  waiting_validation: ['validated'],
  validated: [], // Auto-transitions to in_progress
  in_progress: ['in_delivery', 'delivered', 'canceled', 'ready_to_pick_up'],
  in_delivery: ['delivered'],
  ready_to_pick_up: ['delivered'],
  delivered: [], // Terminal state
  canceled: [], // Terminal state
  canceled_by_system: [],
  canceled_by_admin: [],
  canceled_by_customer: [],
  canceled_by_seller: [],
};

// Get allowed actions based on current order status
function getAllowedActions(currentStatus: OrderStatus): OrderStatus[] {
  return VALID_TRANSITIONS[currentStatus] || [];
}


// Extract customer name from string or object
function getCustomerName(customer: Order['customer']): string {
  if (!customer) return '-';
  if (typeof customer === 'string') return customer;
  if (typeof customer === 'object') {
    if (customer.fullName) return customer.fullName;
    const parts = [customer.firstName, customer.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '-';
  }
  return '-';
}

// Format payment method for display
const paymentMethodLabels: Record<string, string> = {
  in_store: 'En magasin',
  paypal: 'PayPal',
  credit_card: 'Carte bancaire',
  bank_transfer: 'Virement bancaire',
  mobile_money: 'Mobile Money',
};

function getPaymentMethodLabel(method: string | undefined): string {
  if (!method) return '-';
  return paymentMethodLabels[method] || method;
}

export function OrderDetailContainer({ orderId }: OrderDetailContainerProps) {
  const { t } = useTranslation();
  const { currency } = useConfigStore();
  const { data: order, isLoading, error } = useOrder(orderId);
  const { data: timelineData } = useOrderTimeline(orderId);
  const { mutate: updateStatus, isPending: isUpdating } = useUpdateOrderStatus();
  const { mutate: refundOrder, isPending: isRefunding } = useRefundOrder();
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);

  // Combine order tracking with timeline data
  const enrichedTracking = useMemo((): OrderTracking | null => {
    const timeline = timelineData?.orderTracking || order?.tracking?.timeline || [];

    // If no tracking data at all, return null
    if (!order?.tracking && timeline.length === 0) return null;

    // Create tracking object with timeline
    return {
      timeline,
      currentStep: order?.tracking?.currentStep || '',
      nextStep: order?.tracking?.nextStep || null,
      deliveryInfo: order?.tracking?.deliveryInfo || null,
      progress: order?.tracking?.progress || {
        percentage: 0,
        completedSteps: 0,
        totalSteps: timeline.length || 1,
        currentStepIndex: 0,
        stepProgress: `0/${timeline.length || 1}`,
        nextMilestone: '',
      },
      totalEvents: timeline.length,
      hasDeliveryInfo: !!order?.tracking?.deliveryInfo,
      estimatedDelivery: order?.tracking?.estimatedDelivery || '',
      lastUpdate: order?.tracking?.lastUpdate || '',
      publicTrackingUrl: order?.tracking?.publicTrackingUrl || '',
    };
  }, [order?.tracking, timelineData]);

  // Set default selected status to first allowed action when order loads
  useEffect(() => {
    if (order?.status) {
      const allowedActions = getAllowedActions(order.status);
      if (allowedActions.length > 0) {
        setSelectedStatus(allowedActions[0]);
      } else {
        setSelectedStatus(null);
      }
    }
  }, [order?.status]);

  const handleStatusChange = (newStatus: string) => {
    setSelectedStatus(newStatus as OrderStatus);
  };

  const handleConfirmStatusChange = () => {
    if (selectedStatus) {
      updateStatus({ id: orderId, status: selectedStatus });
    }
  };

  // Check if a valid action is selected (any action in the allowed list)
  const hasValidSelection = selectedStatus !== null && getAllowedActions(order?.status || 'waiting_validation').includes(selectedStatus);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-destructive">{t('orders.error')}</p>
        <Link to="/orders">
          <Button variant="outline">
            <ArrowLeft className="size-4 mr-2" />
            {t('common.back')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/orders">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold truncate">{t('orders.orderDetail')} #{order.orderId || orderId}</h1>
            <p className="text-muted-foreground text-sm">{formatDate(order.created_at || order.orderDate)}</p>
          </div>
        </div>
        <Badge variant={statusVariantMap[order.status]} className="shrink-0 self-start sm:self-auto">
          {t(`orders.status.${order.status}`)}
        </Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="size-5" />
                {t('orders.items')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.products?.map((product, index) => (
                  <div key={product.productRefId || index} className="flex items-center justify-between py-3 border-b last:border-0">
                    <div className="flex items-center gap-4">
                      {product.images?.[0]?.url ? (
                        <img
                          src={product.images[0].url}
                          alt={product.name}
                          className="size-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="size-16 rounded-lg bg-muted" />
                      )}
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {t('orders.quantity')}: {product.quantity}
                        </p>
                        {product.selectedSizes?.length > 0 && (
                          <p className="text-sm text-muted-foreground">
                            {t('orders.sizes')}: {product.selectedSizes.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="font-bold">{formatPrice(getAmountFromPriceObject(product.price, currency), currency)}</p>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="space-y-2">
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>{t('orders.total')}</span>
                  <span>{formatPrice(getAmountFromPriceObject(order.total_price, currency), currency)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="size-5" />
                {t('orders.shippingInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('orders.deliveryType')}:</span>
                <span>{(order.delivery_type || order.deliveryType) === 'home' ? 'Livraison à domicile' : 'Retrait en magasin'}</span>
              </div>
              {order.delivery_address && (order.delivery_type || order.deliveryType) === 'home' && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground mb-1">{t('orders.deliveryAddress')}:</p>
                  <p className="text-sm">{order.delivery_address.addressLine1}</p>
                  {order.delivery_address.addressLine2 && (
                    <p className="text-sm">{order.delivery_address.addressLine2}</p>
                  )}
                  <p className="text-sm">
                    {order.delivery_address.city}
                    {order.delivery_address.postalCode && `, ${order.delivery_address.postalCode}`}
                  </p>
                  <p className="text-sm">{order.delivery_address.country}</p>
                  {order.delivery_address.phone && (
                    <p className="text-sm text-muted-foreground mt-1">{order.delivery_address.phone}</p>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('orders.paymentMethod')}:</span>
                <span>{getPaymentMethodLabel(order.payment?.method || order.payment_method)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('orders.paymentStatus')}:</span>
                <Badge variant={(order.payment?.status || order.paymentStatus) === 'completed' ? 'default' : 'destructive'}>
                  {order.payment?.status || order.paymentStatus || '-'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Earnings Info */}
          {order.earnings && (
            <Card className="border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <span>💰</span>
                  {t('orders.yourEarnings', 'Vos gains sur cette commande')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('orders.total', 'Montant total')} :</span>
                  <span className="font-medium">{formatPrice(getAmountFromPriceObject(order.total_price, currency), currency)}</span>
                </div>
                <div className="flex items-center justify-between text-orange-600 dark:text-orange-400">
                  <span>{t('orders.commission', 'Commission')} ({order.earnings.commissionRate}%) :</span>
                  <span>- {formatPrice(getAmountFromPriceObject(order.earnings?.commissionAmount, currency), currency)}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between font-semibold text-green-700 dark:text-green-300">
                  <span>{t('orders.youWillReceive', 'Vous recevrez')} :</span>
                  <span className="text-lg">{formatPrice(getAmountFromPriceObject(order.earnings?.netAmount, currency), currency)}</span>
                </div>
                <p className="text-xs text-muted-foreground pt-2">
                  {t('orders.earningsNote', 'Montant disponible après la période de rétention')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="size-5" />
                {t('orders.customer')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="size-4 text-muted-foreground" />
                <span>{getCustomerName(order.customer)}</span>
              </div>
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">Boutique:</p>
                <p className="font-medium">{order.shop?.name || order.shopName || '-'}</p>
                {order.shop?.phoneNumber && (
                  <p className="text-sm text-muted-foreground">{order.shop.phoneNumber}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Update Status */}
          <Card>
            <CardHeader>
              <CardTitle>{t('orders.updateStatus')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {getAllowedActions(order.status).length > 0 ? (
                <>
                  <Select value={selectedStatus || ''} onValueChange={handleStatusChange} disabled={isUpdating}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getAllowedActions(order.status).map((action) => (
                        <SelectItem key={action} value={action}>
                          {t(`orders.status.${action}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleConfirmStatusChange}
                    disabled={!hasValidSelection || isUpdating}
                    className="w-full"
                  >
                    {isUpdating ? t('common.loading') : t('common.confirm')}
                  </Button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">
                  {t('orders.status.noActionsAvailable', { defaultValue: 'Aucune action disponible pour ce statut' })}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Delivery tracking — uniquement pour les commandes livrées à domicile */}
          {(order.delivery_type || order.deliveryType) === 'home' && (
            <DeliveryTrackingManager orderId={orderId} />
          )}

          {/* Refund — visible uniquement si le paiement est complété */}
          {(order.payment?.status || order.paymentStatus) === 'completed' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('orders.refund.title', { defaultValue: 'Rembourser la commande' })}</CardTitle>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setRefundOpen(true)}
                  disabled={isRefunding}
                >
                  {isRefunding ? t('common.loading') : t('orders.refund.action', { defaultValue: 'Rembourser' })}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Tracking Progress */}
          {enrichedTracking && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="size-5" />
                  {t('orders.timeline')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <OrderTimeline tracking={enrichedTracking} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Messaging Section */}
      <Card className="mt-6">
        <CardContent className="py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center size-12 rounded-full bg-primary/10">
                <MessageSquare className="size-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">{t('messaging.contactCustomer')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('messaging.startHint')}
                </p>
              </div>
            </div>
            <Link to={`/orders/${orderId}/conversation`}>
              <Button className="w-full sm:w-auto">
                <MessageSquare className="size-4 mr-2" />
                {t('messaging.title')}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {refundOpen && (
        <SellerRefundModal
          open
          orderId={order.orderId}
          orderLabel={order.orderId}
          maxRefundAmount={getAmountFromPriceObject(order.total_price, currency)}
          currency={currency}
          isLoading={isRefunding}
          onClose={() => setRefundOpen(false)}
          onConfirm={(body) =>
            refundOrder(
              { orderId: order.orderId, body },
              { onSuccess: () => setRefundOpen(false) }
            )
          }
        />
      )}
    </div>
  );
}
